import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Plus, Pencil } from "lucide-react";
import { adminApi, apiError, PROGRAM_STATUS } from "@/admin/adminApi";
import { PageHead, NotReadyNote } from "@/admin/AdminLayout";
import { btnPrimary, card, Pill } from "@/admin/ui";

function ProgramRow({ program, index, total, onMove }) {
  const st = PROGRAM_STATUS[program.status];
  return (
    <li data-testid={`admin-program-${program.id}`} className={`${card} px-5 md:px-7 py-5 flex flex-col md:flex-row md:items-center gap-4`}>
      <div className="flex md:flex-col gap-1">
        <button type="button" aria-label="Feljebb" disabled={index === 0} onClick={() => onMove(index, -1)} data-testid={`admin-program-up-${program.id}`} className="p-1.5 text-graphite-soft hover:text-gold disabled:opacity-25">
          <ArrowUp className="w-4 h-4" />
        </button>
        <button type="button" aria-label="Lejjebb" disabled={index === total - 1} onClick={() => onMove(index, 1)} data-testid={`admin-program-down-${program.id}`} className="p-1.5 text-graphite-soft hover:text-gold disabled:opacity-25">
          <ArrowDown className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[10.5px] tracking-[0.28em] uppercase text-gold">{program.number}</p>
        <p className="mt-1.5 font-serif-display text-[24px] leading-tight text-graphite tracking-[0.03em]">{program.title}</p>
        {program.subtitle && <p className="mt-1 font-serif-display italic text-[16px] text-graphite-soft">{program.subtitle}</p>}
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <Pill tone={st.tone} testId={`admin-program-status-${program.id}`}>{st.label}</Pill>
        <span className="font-body text-[13px] text-graphite-soft">{program.session_count} időpont</span>
        <Link to={`/admin/programok/${program.id}`} data-testid={`admin-program-edit-${program.id}`} className="inline-flex items-center gap-2 font-body text-[13px] text-graphite border-b border-[#A07A28] pb-0.5 hover:text-gold">
          <Pencil className="w-3.5 h-3.5" /> Szerkesztés és időpontok
        </Link>
      </div>
    </li>
  );
}

export default function AdminPrograms() {
  const [programs, setPrograms] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/programs").then((r) => setPrograms(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const move = async (index, dir) => {
    const next = [...programs];
    [next[index], next[index + dir]] = [next[index + dir], next[index]];
    setPrograms(next);
    try {
      await adminApi.put("/admin/programs-order", { ids: next.map((p) => p.id) });
      toast.success("A sorrend elmentve.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-programs-page">
      <PageHead eyebrow="Programok" title="Programok és időpontok">
        <Link to="/admin/programok/uj" data-testid="admin-program-new-btn" className={btnPrimary}>
          <Plus className="w-4 h-4" /> Új program
        </Link>
      </PageHead>
      <NotReadyNote testId="admin-programs-public-note">
        Az itt mentett programok és nyitott időpontok közvetlenül megjelennek a nyilvános „Aktuális programok” oldalon.
        A piszkozat és az archív programok nem láthatók. A jelentkezés megnyitását a Jelentkezők menüben kapcsolhatod be.
      </NotReadyNote>
      {!programs ? (
        <p className="mt-8 font-body text-graphite-soft">Betöltés…</p>
      ) : (
        <ul className="mt-8 space-y-4">
          {programs.map((p, i) => (
            <ProgramRow key={p.id} program={p} index={i} total={programs.length} onMove={move} />
          ))}
        </ul>
      )}
    </div>
  );
}
